import React, { useEffect, useState } from 'react';
import { PartyPopper, Sparkles, Check, X } from 'lucide-react';

/**
 * Fullscreen celebration popup that fires when a coupon is successfully applied.
 * Shows a confetti burst + congratulations message + the unlocked discount.
 * Auto-dismisses after ~3.5s but is also closable.
 */
const CONFETTI_COLORS = ['#2D5A3D', '#D9A441', '#F5EDE0', '#9CC4A8', '#E8C77A'];

const CouponCelebration = ({ open, onClose, code, percentOff }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMounted(true);
    const t = setTimeout(() => {
      onClose?.();
    }, 4200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  // Pre-compute confetti pieces with deterministic randomness per mount
  const pieces = Array.from({ length: 60 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 2.2 + Math.random() * 1.6,
    rotate: Math.random() * 360,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + Math.random() * 8,
    shape: i % 3,
  }));

  return (
    <div
      data-testid="coupon-celebration-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(20, 30, 22, 0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      {/* Confetti layer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {pieces.map((p) => (
          <span
            key={p.id}
            className="absolute top-[-12px] block coupon-confetti-piece"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * (p.shape === 1 ? 0.5 : 1)}px`,
              backgroundColor: p.color,
              transform: `rotate(${p.rotate}deg)`,
              borderRadius: p.shape === 2 ? '50%' : '2px',
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Card */}
      <div
        data-testid="coupon-celebration-card"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-[460px] rounded-3xl px-7 py-10 text-center coupon-celebration-pop ${mounted ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 30px 80px rgba(0,0,0,0.35), 0 0 0 6px rgba(217,164,65,0.18)',
          border: '2px solid #D9A441',
        }}
      >
        <button
          data-testid="coupon-celebration-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5"
          style={{ color: '#2D5A3D' }}
        >
          <X size={18} />
        </button>

        {/* Animated medal */}
        <div className="flex justify-center -mt-2 mb-3">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center coupon-celebration-medal"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #F0CE7A 0%, #D9A441 55%, #B0801F 100%)',
              boxShadow: '0 8px 24px rgba(217,164,65,0.45), inset 0 -3px 6px rgba(0,0,0,0.15)',
            }}
          >
            <PartyPopper size={38} color="#FFFFFF" strokeWidth={2.2} />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-1">
          <Sparkles size={16} color="#D9A441" />
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold" style={{ color: '#D9A441' }}>
            Discount unlocked
          </p>
          <Sparkles size={16} color="#D9A441" />
        </div>

        <h3
          className="font-serif text-[36px] md:text-[44px] leading-[1.05] mt-1"
          style={{ color: '#2D5A3D' }}
        >
          Congratulations!
        </h3>

        <p className="mt-3 text-[15px] leading-snug" style={{ color: '#2C2418' }}>
          Your code <span className="font-bold" style={{ color: '#2D5A3D' }}>{code}</span> just unlocked
        </p>

        <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full coupon-celebration-pulse"
          style={{
            backgroundColor: '#2D5A3D',
            color: '#FFFFFF',
            boxShadow: '0 10px 22px rgba(45,90,61,0.32)',
          }}
        >
          <Check size={18} />
          <span className="font-bold tracking-wide text-[18px]">{percentOff}% OFF every bundle</span>
        </div>

        <p className="mt-5 text-[12px] italic opacity-80" style={{ color: '#2C2418' }}>
          Pick your bundle below — the new price is already live.
        </p>

        <button
          data-testid="coupon-celebration-cta"
          onClick={onClose}
          className="mt-6 w-full py-3.5 rounded-full text-[12px] uppercase tracking-[0.2em] font-bold btn-hover"
          style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
        >
          Shop with my discount
        </button>
      </div>
    </div>
  );
};

export default CouponCelebration;
